// Relevé d'activité — a monthly activity statement PDF for the freelance's
// accountant. Implements SundialReleve.dc.html: money-first (HT/TVA/TTC lead,
// days are supporting evidence), greyscale-safe (hierarchy from size/weight/
// rules, never colour alone), one A4 page.
//
// Client-side jsPDF, vector primitives only — no rasterised logo, so the sun
// glyph is drawn from a circle and eight rays and stays crisp at any DPI.

import { jsPDF } from "jspdf";
import { formatEuros, type DayStatus } from "@sundial/core";

export interface ReleveIdentity {
  name: string;
  siret: string;
  address: string;
}

export interface ReleveDay {
  date: string; // YYYY-MM-DD
  status: DayStatus;
  billable: number;
  note?: string | null;
}

export interface ReleveInput {
  identity: ReleveIdentity;
  monthLabel: string; // e.g. "Juin 2026"
  generatedOn: string; // e.g. "20/07/2026"
  billableDays: number;
  htCents: number;
  vatCents: number;
  ttcCents: number;
  receivedCents: number;
  owedCents: number;
  /** True when the franchise-en-base applies: TVA 0 and HT = TTC. */
  franchise: boolean;
  days: ReleveDay[];
  /** Invoices (kept in Abby) attached to the range's payments. Optional. */
  invoices?: ReleveInvoice[];
  /** When the relevé is scoped to one client: its "Facturé à" identity. The
      money above is then that client's, and labels gain a "· {name}" suffix. */
  client?: { name: string; siret?: string; address?: string };
  /** All-clients view: per-client HT/days, for the "Répartition par client"
      table. Ignored when `client` is set. */
  breakdown?: { name: string; days: number; htCents: number }[];
}

export interface ReleveInvoice {
  period: string | null;
  ref: string;
  /** Validated http(s) link when present — becomes a clickable ref in the PDF. */
  url?: string | null;
}

// Palette — the design's exact values.
const NAVY = "#1B2A4A";
const ORANGE = "#F47A20";
const GREY = "#5A6072";
const MUTE = "#8A8F9C";
const HAIR = "#D8DCE4";
const ZEBRA = "#F7F4EF";
const ROW_LINE = "#ECEEF2";
const GHOST = "#B7BCC7"; // 0,00 € in the empty state

const STATUT: Record<DayStatus, string> = {
  full: "Jour complet",
  half: "Demi-journée",
  none: "—",
  sick: "Maladie",
  holiday: "Jour férié",
};
const WARM_GREY = "#9A8E7B";

/**
 * jsPDF's built-in Helvetica is WinAnsi-encoded: it has no ●◐○✕◆, no → arrow,
 * and no narrow/thin no-break space (which fr-FR uses as a thousands separator).
 * Those all render as garbage (%A, !', 7/800). So status markers are drawn as
 * vector shapes, and every dynamic string is normalised to ASCII-safe spacing.
 */
function drawMarker(doc: jsPDF, cx: number, cy: number, status: DayStatus, bg: string): void {
  const r = 2.5;
  if (status === "full") {
    doc.setFillColor(NAVY);
    doc.circle(cx, cy, r, "F");
  } else if (status === "half") {
    doc.setFillColor(NAVY);
    doc.circle(cx, cy, r, "F");
    doc.setFillColor(bg); // mask the right half back to the row background
    doc.rect(cx, cy - r - 0.4, r + 0.8, 2 * r + 0.8, "F");
    doc.setDrawColor(NAVY);
    doc.setLineWidth(0.5);
    doc.circle(cx, cy, r, "S");
  } else if (status === "none") {
    doc.setDrawColor(WARM_GREY);
    doc.setLineWidth(0.6);
    doc.circle(cx, cy, r, "S");
  } else if (status === "sick") {
    doc.setDrawColor(WARM_GREY);
    doc.setLineWidth(0.7);
    doc.line(cx - r, cy - r, cx + r, cy + r);
    doc.line(cx - r, cy + r, cx + r, cy - r);
  } else {
    // holiday — a filled square (distinct from the circles, no arc/polyline API)
    doc.setFillColor(WARM_GREY);
    doc.rect(cx - r, cy - r, 2 * r, 2 * r, "F");
  }
}

/** Normalise a dynamic string to what WinAnsi Helvetica can render. */
function S(s: string): string {
  return s.replace(/[\u202f\u2009\u00a0]/g, " ").replace(/\u2192/g, "-");
}

/** Money, with the thousands separator forced to a plain space. */
function money(cents: number): string {
  return S(formatEuros(cents));
}

function weekdayFr(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map((n) => parseInt(n, 10));
  const wd = new Intl.DateTimeFormat("fr-FR", { weekday: "short" }).format(new Date(y!, m! - 1, d!));
  return wd.charAt(0).toUpperCase() + wd.slice(1, 3); // "Lun", "Mar", …
}

function ddmm(dateStr: string): string {
  const [, m, d] = dateStr.split("-");
  return `${d}/${m}`;
}

/** The Sundial sun: a filled circle + eight rays. Vector, so crisp at any size. */
function sunGlyph(doc: jsPDF, cx: number, cy: number, r: number, color: string): void {
  doc.setFillColor(color);
  doc.setDrawColor(color);
  doc.circle(cx, cy, r, "F");
  const inner = r * 1.5;
  const outer = r * 2.3;
  doc.setLineWidth(r * 0.28);
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4;
    doc.line(cx + Math.cos(a) * inner, cy + Math.sin(a) * inner, cx + Math.cos(a) * outer, cy + Math.sin(a) * outer);
  }
}

/** A basis pill — HT (outline), TVA (dashed), or TTC (filled). Shape carries the
    meaning, so the distinction survives greyscale. */
function basisPill(
  doc: jsPDF,
  x: number,
  yBaseline: number,
  kind: "HT" | "TVA" | "TTC",
  tone: string,
): number {
  const label = kind;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6);
  const w = doc.getTextWidth(label) + 6;
  const h = 9;
  const top = yBaseline - h + 2;
  if (kind === "TTC") {
    doc.setFillColor(tone);
    doc.roundedRect(x, top, w, h, 1.5, 1.5, "F");
    doc.setTextColor("#ffffff");
  } else {
    doc.setDrawColor(tone);
    doc.setLineWidth(0.6);
    if (kind === "TVA") doc.setLineDashPattern([1.2, 1], 0);
    doc.roundedRect(x, top, w, h, 1.5, 1.5, "S");
    doc.setLineDashPattern([], 0);
    doc.setTextColor(tone);
  }
  doc.text(label, x + 3, yBaseline - 1.5);
  return w;
}

const PAGE_W = 595.28;
const PAGE_H = 841.89;
const MX = 44; // page margin
const CONTENT_W = PAGE_W - MX * 2;

/** Header identity block + navy rule. Returns the y below the rule. */
function drawHeader(doc: jsPDF, input: ReleveInput, title: string): number {
  const top = 48;
  // Left: the freelance's own identity.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(NAVY);
  doc.text(input.identity.name || "—", MX, top);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  if (input.identity.siret) doc.text(`SIRET ${input.identity.siret}`, MX, top + 13);
  doc.setTextColor(MUTE);
  doc.setFontSize(7.5);
  if (input.identity.address) doc.text(input.identity.address, MX, top + 23);

  // Right: document label, month, generated-on.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(MUTE);
  doc.text(title.toUpperCase(), PAGE_W - MX, top - 4, { align: "right", charSpace: 0.6 });
  doc.setTextColor(NAVY);
  // Fit the label: a date range ("2026-06-01 - 2026-06-30") is far wider than a
  // month name, so shrink it rather than let it overrun into the identity block.
  const label = S(input.monthLabel);
  const maxLabelW = 320;
  let fs = 23;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fs);
  const w = doc.getTextWidth(label);
  if (w > maxLabelW) {
    fs = Math.max(11, Math.floor((fs * maxLabelW) / w));
    doc.setFontSize(fs);
  }
  doc.text(label, PAGE_W - MX, top + 15, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(MUTE);
  doc.text(`Généré le ${input.generatedOn}`, PAGE_W - MX, top + 27, { align: "right" });

  const ruleY = top + 36;
  doc.setFillColor(NAVY);
  doc.rect(MX, ruleY, CONTENT_W, 1.4, "F");
  return ruleY + 22;
}

/** A single money cell: uppercase label, big value + pill, caption. */
function moneyCell(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  label: string,
  value: string,
  pill: "HT" | "TVA" | "TTC" | null,
  opts: { big: boolean; ghost?: boolean; caption?: string; underline?: boolean; size?: number },
): void {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(MUTE);
  doc.text(label.toUpperCase(), x, y, { charSpace: 0.5 });

  const valColor = opts.ghost ? GHOST : pill === "TVA" ? GREY : NAVY;
  // The design's hierarchy: HT/TTC dominate at ~24pt, TVA is lighter (18pt,
  // weight 600 → helvetica normal in grey), the second row sits at 16pt.
  const size = opts.size ?? (opts.big ? 24 : 16);
  doc.setFont("helvetica", pill === "TVA" ? "normal" : "bold");
  doc.setFontSize(size);
  doc.setTextColor(valColor);
  const vy = y + (opts.big ? 21 : 16);
  doc.text(value, x, vy);
  if (pill) {
    const vw = doc.getTextWidth(value);
    basisPill(doc, x + vw + 7, vy, pill, opts.ghost ? GHOST : pill === "TVA" ? "#9AA0AE" : NAVY);
  }
  if (opts.underline) {
    doc.setFillColor(ORANGE);
    doc.rect(x, vy + 6, 72, 2.2, "F");
  }
  if (opts.caption) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(MUTE);
    doc.text(opts.caption, x, vy + (opts.underline ? 16 : 14));
  }
}

/** Footer: sun glyph + "Généré par Sundial" · date · page x/y. Every page. */
function drawFooter(doc: jsPDF, input: ReleveInput, page: number, pages: number): void {
  const y = PAGE_H - 30;
  doc.setDrawColor("#C9CFDA");
  doc.setLineWidth(0.5);
  doc.line(MX, y, PAGE_W - MX, y);
  sunGlyph(doc, MX + 4, y + 9, 2.2, ORANGE);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor("#6B7180");
  doc.text("Généré par Sundial", MX + 12, y + 11);
  doc.text(input.generatedOn, PAGE_W / 2, y + 11, { align: "center" });
  doc.text(`page ${page}/${pages}`, PAGE_W - MX, y + 11, { align: "right" });
}

/** Build the relevé and trigger a direct download. */
export function exportRelevePdf(input: ReleveInput): void {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  let y = drawHeader(doc, input, "Relevé d'activité");
  const empty = input.days.length === 0;
  // When scoped to a client, money labels gain a "· {name}" suffix.
  const suffix = input.client ? ` · ${S(input.client.name)}` : "";

  // "Facturé à" block — a second identity, distinct from the émetteur.
  if (input.client) {
    y = drawClientBlock(doc, y, input.client);
  }

  // Money hero — the three-column HT / TVA / TTC row.
  const colW = CONTENT_W / 3;
  const ghost = empty;
  moneyCell(doc, MX, y, colW, `Total HT${suffix}`, money(input.htCents), "HT", {
    big: true,
    ghost,
    caption: empty ? "Base imposable" : `Base imposable · ${fmtDays(input.billableDays)} j`,
  });
  doc.setDrawColor(HAIR);
  doc.setLineWidth(0.5);
  doc.line(MX + colW, y - 4, MX + colW, y + 44);
  moneyCell(doc, MX + colW + 18, y, colW, input.franchise ? "TVA" : "TVA (20 %)", money(input.vatCents), "TVA", {
    big: false,
    size: 18,
    ghost,
    caption: input.franchise ? "Non applicable" : "Collectée pour l'État",
  });
  doc.line(MX + colW * 2, y - 4, MX + colW * 2, y + 44);
  moneyCell(doc, MX + colW * 2 + 18, y, colW, `Total TTC${suffix}`, money(input.ttcCents), "TTC", {
    big: true,
    ghost,
    underline: !empty,
  });
  y += 60;

  if (empty) {
    drawEmptyBody(doc, y, input.client ? S(input.client.name) : null, input.monthLabel);
    drawFooter(doc, input, 1, 1);
    doc.save(fileName(input));
    return;
  }

  // Person-level tax note — TVA/URSSAF are never split by client.
  if (input.client) {
    doc.setFillColor("#F6F4EF");
    doc.rect(MX, y - 12, CONTENT_W, 20, "F");
    doc.setFillColor(HAIR);
    doc.rect(MX, y - 12, 2.5, 20, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(GREY);
    doc.text("TVA et cotisations URSSAF sont calculées sur l'activité globale et ne sont pas ventilées par client.", MX + 8, y);
    y += 22;
  }

  // Répartition par client — all-clients view with a per-client HT breakdown.
  if (!input.client && input.breakdown && input.breakdown.length > 1) {
    y = drawBreakdown(doc, y, input.breakdown);
  }

  // Franchise banner — only when TVA is 0 by the franchise, to explain HT = TTC.
  if (input.franchise) {
    doc.setFillColor("#F6F4EF");
    doc.rect(MX, y - 12, 360, 20, "F");
    doc.setFillColor(ORANGE);
    doc.rect(MX, y - 12, 2.5, 20, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(GREY);
    doc.text("Franchise en base de TVA · art. 293 B du CGI — TVA non applicable. HT et TTC sont égaux.", MX + 8, y);
    y += 22;
  }

  // Second money row: jours facturables / encaissé / reste à payer.
  doc.setDrawColor(HAIR);
  doc.line(MX, y - 6, PAGE_W - MX, y - 6);
  y += 6;
  moneyCell(doc, MX, y, colW, "Jours facturables", fmtDays(input.billableDays), null, { big: false });
  doc.line(MX + colW, y - 4, MX + colW, y + 34);
  moneyCell(doc, MX + colW + 18, y, colW, "Encaissé", money(input.receivedCents), "TTC", { big: false });
  doc.line(MX + colW * 2, y - 4, MX + colW * 2, y + 34);
  moneyCell(doc, MX + colW * 2 + 18, y, colW, "Reste à payer", money(input.owedCents), "TTC", { big: false });
  y += 54;

  // Day table — the supporting evidence.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(MUTE);
  doc.text(`DÉTAIL DES JOURS${input.client ? ` · ${S(input.client.name).toUpperCase()}` : ""}`, MX, y, { charSpace: 0.6 });
  doc.text(`Pièce justificative · ${input.days.length} entrées`, PAGE_W - MX, y, { align: "right" });
  y += 10;

  const cols = [MX, MX + 70, MX + 128, MX + 290]; // Date, Jour, Statut, Note starts
  doc.setFillColor(NAVY);
  doc.rect(MX, y + 4, CONTENT_W, 1.2, "F");
  doc.setFontSize(7);
  doc.text("DATE", cols[0]!, y, { charSpace: 0.4 });
  doc.text("JOUR", cols[1]!, y, { charSpace: 0.4 });
  doc.text("STATUT", cols[2]!, y, { charSpace: 0.4 });
  doc.text("NOTE", cols[3]!, y, { charSpace: 0.4 });
  y += 14;

  const rowH = 15;
  input.days.forEach((d, i) => {
    if (i % 2 === 1) {
      doc.setFillColor(ZEBRA);
      doc.rect(MX, y - rowH + 5, CONTENT_W, rowH, "F");
    }
    doc.setDrawColor(ROW_LINE);
    doc.setLineWidth(0.4);
    doc.line(MX, y + 5, PAGE_W - MX, y + 5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(NAVY);
    doc.text(ddmm(d.date), cols[0]!, y);
    doc.setTextColor(GREY);
    doc.text(weekdayFr(d.date), cols[1]!, y);
    drawMarker(doc, cols[2]! + 3, y - 3, d.status, i % 2 === 1 ? ZEBRA : "#FFFFFF");
    doc.setFontSize(9);
    doc.setTextColor(NAVY);
    doc.text(STATUT[d.status], cols[2]! + 12, y);
    doc.setTextColor(GREY);
    doc.text(clip(doc, S(d.note ?? ""), PAGE_W - MX - cols[3]!), cols[3]!, y);
    y += rowH;
  });

  // Legend — vector markers, matching the table.
  y += 6;
  doc.setFontSize(7);
  let lx = MX;
  ([["full", "Jour complet"], ["half", "Demi-journée"], ["none", "Congé"], ["sick", "Maladie"], ["holiday", "Jour férié"]] as const).forEach(([st, l]) => {
    drawMarker(doc, lx + 2, y - 2, st, "#FFFFFF");
    doc.setTextColor(GREY);
    doc.text(l, lx + 9, y);
    lx += doc.getTextWidth(l) + 26;
  });

  // Factures rattachées — the Abby invoices these payments settle. Sundial does
  // not issue them; it records the link so the statement points at the invoice.
  const invoices = input.invoices ?? [];
  if (invoices.length > 0) {
    y += 26;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(MUTE);
    doc.text("FACTURES RATTACHÉES", MX, y, { charSpace: 0.6 });
    doc.text("Émises dans Abby", PAGE_W - MX, y, { align: "right" });
    y += 10;
    doc.setFillColor(NAVY);
    doc.rect(MX, y + 4, CONTENT_W, 1.2, "F");
    doc.setFontSize(7);
    doc.text("PÉRIODE", MX, y, { charSpace: 0.4 });
    doc.text("FACTURE N°", MX + 130, y, { charSpace: 0.4 });
    y += 14;
    invoices.forEach((inv, i) => {
      if (i % 2 === 1) {
        doc.setFillColor(ZEBRA);
        doc.rect(MX, y - 10, CONTENT_W, 15, "F");
      }
      doc.setDrawColor(ROW_LINE);
      doc.setLineWidth(0.4);
      doc.line(MX, y + 5, PAGE_W - MX, y + 5);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(GREY);
      doc.text(inv.period ?? "—", MX, y);
      doc.setTextColor(inv.url ? "#0F5FBF" : NAVY);
      if (inv.url) doc.textWithLink(clip(doc, S(inv.ref), CONTENT_W - 140), MX + 130, y, { url: inv.url });
      else doc.text(clip(doc, S(inv.ref), CONTENT_W - 140), MX + 130, y);
      y += 15;
    });
  }

  drawFooter(doc, input, 1, 1);
  doc.save(fileName(input));
}

function drawEmptyBody(doc: jsPDF, y: number, clientName: string | null, monthLabel: string): void {
  const boxY = y + 20;
  doc.setDrawColor("#C9CFDA");
  doc.setLineDashPattern([2, 2], 0);
  doc.setLineWidth(0.6);
  doc.roundedRect(MX, boxY, CONTENT_W, 110, 6, 6, "S");
  doc.setLineDashPattern([], 0);
  sunGlyph(doc, PAGE_W / 2, boxY + 40, 4, MUTE);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(GREY);
  const headline = clientName
    ? `Aucun jour facturé à ${clientName} en ${monthLabel}`
    : "Aucun jour saisi pour ce mois";
  doc.text(headline, PAGE_W / 2, boxY + 68, { align: "center" });
  doc.setFontSize(8);
  doc.setTextColor(MUTE);
  doc.text("Ajoutez vos journées dans Sundial pour générer le détail et les totaux.", PAGE_W / 2, boxY + 82, {
    align: "center",
  });
}

/**
 * "Facturé à" block for a client-scoped relevé — a second identity distinct
 * from the émetteur, with a navy left rule and a "Filtré · 1 client" chip.
 */
function drawClientBlock(doc: jsPDF, y: number, client: { name: string; siret?: string; address?: string }): number {
  const h = 34;
  doc.setFillColor("#FFFFFF");
  doc.setDrawColor(HAIR);
  doc.setLineWidth(0.7);
  doc.roundedRect(MX, y, CONTENT_W, h, 4, 4, "S");
  doc.setFillColor(NAVY);
  doc.rect(MX, y, 3, h, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.5);
  doc.setTextColor(MUTE);
  doc.text("FACTURÉ À", MX + 13, y + 13, { charSpace: 0.7 });
  doc.setFontSize(12.5);
  doc.setTextColor(NAVY);
  doc.text(S(client.name), MX + 13, y + 26);
  const meta = [client.siret ? `SIRET ${S(client.siret)}` : "", client.address ? S(client.address) : ""]
    .filter(Boolean)
    .join("   ·   ");
  if (meta) {
    const nameW = doc.getTextWidth(S(client.name));
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(MUTE);
    doc.text(clip(doc, meta, CONTENT_W - nameW - 130), MX + 13 + nameW + 12, y + 26);
  }
  // "Filtré · 1 client" chip, right-aligned.
  const chip = "Filtré · 1 client";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.5);
  const cw = doc.getTextWidth(chip) + 12;
  doc.setDrawColor("#C9CFDA");
  doc.setLineWidth(0.6);
  doc.roundedRect(PAGE_W - MX - cw - 12, y + 11, cw, 13, 3, 3, "S");
  doc.setTextColor(GREY);
  doc.text(chip, PAGE_W - MX - cw - 6, y + 19.5, { charSpace: 0.4 });
  return y + h + 16;
}

/**
 * "Répartition par client" — an all-clients view of HT per client, so a
 * multi-client month reconciles against its per-client totals.
 */
function drawBreakdown(doc: jsPDF, y: number, rows: { name: string; days: number; htCents: number }[]): number {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(MUTE);
  doc.text("RÉPARTITION PAR CLIENT", MX, y, { charSpace: 0.6 });
  doc.text("HT attribué · hors TVA", PAGE_W - MX, y, { align: "right" });
  y += 10;
  doc.setFillColor(NAVY);
  doc.rect(MX, y + 4, CONTENT_W, 1.2, "F");
  doc.setFontSize(7);
  doc.text("CLIENT", MX, y, { charSpace: 0.4 });
  doc.text("JOURS", PAGE_W - MX - 150, y, { align: "right", charSpace: 0.4 });
  doc.text("HT", PAGE_W - MX, y, { align: "right", charSpace: 0.4 });
  y += 14;
  const rowH = 15;
  let totalDays = 0;
  let totalHt = 0;
  rows.forEach((r, i) => {
    totalDays += r.days;
    totalHt += r.htCents;
    if (i % 2 === 1) {
      doc.setFillColor(ZEBRA);
      doc.rect(MX, y - rowH + 5, CONTENT_W, rowH, "F");
    }
    doc.setDrawColor(ROW_LINE);
    doc.setLineWidth(0.4);
    doc.line(MX, y + 5, PAGE_W - MX, y + 5);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(NAVY);
    doc.text(clip(doc, S(r.name), CONTENT_W - 210), MX, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(GREY);
    doc.text(fmtDays(r.days), PAGE_W - MX - 150, y, { align: "right" });
    doc.setTextColor(NAVY);
    doc.text(money(r.htCents), PAGE_W - MX, y, { align: "right" });
    y += rowH;
  });
  // Total row.
  doc.setDrawColor(NAVY);
  doc.setLineWidth(1);
  doc.line(MX, y - rowH + 5, PAGE_W - MX, y - rowH + 5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(NAVY);
  doc.text("Total", MX, y);
  doc.text(fmtDays(totalDays), PAGE_W - MX - 150, y, { align: "right" });
  doc.text(money(totalHt), PAGE_W - MX, y, { align: "right" });
  return y + 26;
}

function fmtDays(n: number): string {
  return S(n.toLocaleString("fr-FR", { maximumFractionDigits: 1 }));
}

function clip(doc: jsPDF, text: string, maxW: number): string {
  if (doc.getTextWidth(text) <= maxW) return text;
  let t = text;
  while (t.length > 1 && doc.getTextWidth(t + "…") > maxW) t = t.slice(0, -1);
  return t + "…";
}

function slugify(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function fileName(input: ReleveInput): string {
  const month = slugify(input.monthLabel);
  const client = input.client ? `${slugify(input.client.name)}-` : "";
  return `releve-${client}${month}.pdf`;
}
